import { useState, useEffect } from 'react';
import { logger } from '@/utils/logger';
import { toast } from 'sonner';
import { useCalendarStore } from '@/stores/calendarStore';
import { useUserStore } from '@/stores/userStore';
import {
  updateVideoContent,
  updateVideoContentInstagramCrosspost,
  updateVideoContentVersionEnabled,
} from '@/services/data/videoContent';
import {
  updatePublication,
  getPublicationsForVideo,
} from '@/services/data/videoContentPublications';
import { deleteMetricoolSchedule } from '@/services/data/metricoolScheduler';
import {
  crossPostToChannels,
  type PublishChannel,
} from '@/services/data/publishContent';
import { getUserPlanningPreferences } from '@/services/data/planningPreferences';
import { getConnectedPlatforms } from '@/services/data/socialConnections';
import { supabase } from '@/integrations/supabase/client';
import { groupPublicationsByTime, getAbTestingDate } from '../types';
import type { ContentEvent, SocialConnection, PublicationItem } from '../types';
import type { Channel } from '@/services/business/calendar';

// ---------------------------------------------------------------------------
// Utility: compute optimistic calendar dates after a publication update
// ---------------------------------------------------------------------------
export function computePublicationEventDates(
  publications: Array<{ channel: Channel; date: Date; time: string }>,
  event: ContentEvent,
) {
  const nonYtPubs = publications.filter((p) => p.channel !== 'youtube');
  const ytPubs = publications.filter((p) => p.channel === 'youtube');

  const newContentDate =
    nonYtPubs.length > 0
      ? new Date(nonYtPubs.reduce((e, p) => (p.date < e.date ? p : e)).date)
      : null;

  const newAbDate =
    ytPubs.length > 0
      ? (() => {
          const d = new Date(ytPubs[0].date);
          d.setDate(d.getDate() - 1);
          return d;
        })()
      : null;

  const isAbEvent = Boolean(event.linkedContentId);
  const dateForThisEvent = isAbEvent ? newAbDate : newContentDate;

  const newCalendarId = isAbEvent
    ? ((event as any).calendarId ?? event.id)
    : newContentDate
      ? `${event.id}_${newContentDate.toISOString().split('T')[0]}`
      : ((event as any).calendarId ?? event.id);

  return { newContentDate, newAbDate, dateForThisEvent, newCalendarId };
}

// ---------------------------------------------------------------------------
// Hook options
// ---------------------------------------------------------------------------
export interface UseContentDetailOptions {
  isOpen: boolean;
  userId: string | null;
  isAdmin: boolean;
  connectedPlatforms: SocialConnection[];
  onValidate?: () => void;
  onClose: () => void;
  onEventUpdate?: (id: string, patch: Partial<ContentEvent>) => void;
}

// ---------------------------------------------------------------------------
// useContentDetail
// ---------------------------------------------------------------------------
export function useContentDetail(
  event: ContentEvent | null,
  options: UseContentDetailOptions,
) {
  const { isOpen, userId, isAdmin, connectedPlatforms, onValidate, onClose, onEventUpdate } =
    options;

  const { updateVideoPublications } = useCalendarStore();
  const { profiles, fetchUserProfile } = useUserStore();

  // ---- Edit fields ----
  const [editingField, setEditingField] = useState<'title' | 'description' | null>(null);
  const [editTitle, setEditTitle] = useState(event?.title ?? '');
  const [editDescription, setEditDescription] = useState(event?.description ?? '');

  // ---- Schedule ----
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [allItems, setAllItems] = useState<PublicationItem[]>([]);
  const [checkedDates, setCheckedDates] = useState<Record<string, boolean>>({});

  // ---- Validation ----
  const [isValidating, setIsValidating] = useState(false);

  // ---- Publish ----
  const [isPublishing, setIsPublishing] = useState(false);
  const [showPublishMenu, setShowPublishMenu] = useState(false);

  // ---- Close confirm ----
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // ---- Crosspost ----
  const [globalCrosspostEnabled, setGlobalCrosspostEnabled] = useState(false);
  const [crosspostDisabled, setCrosspostDisabled] = useState(
    event?.instagramCrosspostDisabled ?? false,
  );

  // ---- Versions (multihook) ----
  const [versionsEnabled, setVersionsEnabled] = useState<boolean[]>([
    event?.versionAEnabled !== false,
    event?.versionBEnabled !== false,
    event?.versionCEnabled !== false,
  ]);

  // ---- Owner platforms (admin view) ----
  const [ownerPlatforms, setOwnerPlatforms] = useState<SocialConnection[]>([]);

  // ---- Video ----
  const [activeVideoUrl, setActiveVideoUrl] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // User profile
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (userId && !profiles[userId]) {
      fetchUserProfile(userId);
    }
  }, [userId, profiles, fetchUserProfile]);

  const userProfile = userId ? profiles[userId] : null;

  // ---------------------------------------------------------------------------
  // Sync edit values when event changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    setEditTitle(event?.title ?? '');
    setEditDescription(event?.description ?? '');
    setEditingField(null);
    setCrosspostDisabled(event?.instagramCrosspostDisabled ?? false);
    setVersionsEnabled([
      event?.versionAEnabled !== false,
      event?.versionBEnabled !== false,
      event?.versionCEnabled !== false,
    ]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(event as any)?.calendarId ?? event?.id]);

  // ---------------------------------------------------------------------------
  // Global Instagram crosspost setting
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!userId || !isOpen) return;
    getUserPlanningPreferences(userId).then((result) => {
      if (result.success && result.data) {
        setGlobalCrosspostEnabled(result.data.instagram_crosspost_enabled ?? false);
      }
    });
  }, [userId, isOpen]);

  // ---------------------------------------------------------------------------
  // Admin: fetch owner's connected platforms when none provided
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!isOpen || connectedPlatforms.length > 0 || !event?.user_id || !isAdmin) return;
    getConnectedPlatforms(event.user_id).then((result) => {
      if (result.success && result.data) {
        setOwnerPlatforms(result.data as SocialConnection[]);
      }
    });
  }, [isOpen, connectedPlatforms.length, event?.user_id, isAdmin]);

  const effectivePlatforms =
    connectedPlatforms.length > 0 ? connectedPlatforms : ownerPlatforms;

  // ---------------------------------------------------------------------------
  // Build allItems from publicationTimes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!event?.publicationTimes || event.publicationTimes.length === 0) {
      setAllItems([]);
      return;
    }

    // Disabled channels are persisted in the DB for continuity but must not
    // appear in the schedule display, affect the published/checked state, or
    // be touched by toggleDateCheck.
    const activePubs = event.publicationTimes.filter(p => p.pubStatus !== 'disabled');
    const regularGroups = groupPublicationsByTime(activePubs, event.isMultihook);
    const abTestingPub = event.isMultihook ? getAbTestingDate(activePubs) : null;

    const items: PublicationItem[] = [];

    regularGroups.forEach((group) => {
      const itemDate = new Date(group[0].date);
      const [h, m] = group[0].time.split(':').map(Number);
      itemDate.setHours(h, m, 0, 0);
      items.push({ type: 'regular', date: itemDate, time: group[0].time, data: group });
    });

    if (abTestingPub) {
      const abDate = new Date(abTestingPub.date);
      const [h, m] = abTestingPub.time.split(':').map(Number);
      abDate.setHours(h, m, 0, 0);
      items.push({ type: 'ab', date: abDate, time: abTestingPub.time, data: abTestingPub });
    }

    items.sort((a, b) => a.date.getTime() - b.date.getTime());
    setAllItems(items);
  }, [event?.publicationTimes, event?.isMultihook]);

  // ---------------------------------------------------------------------------
  // Initialize checkedDates from publication statuses
  // ---------------------------------------------------------------------------
  const pubStatusSignature = allItems
    .map((item) => {
      const key = `${item.date.toISOString()}-${item.time}`;
      if (item.type === 'regular' && Array.isArray(item.data)) {
        const statuses = item.data
          .map((pub) => `${pub.pubId}:${pub.pubStatus ?? 'scheduled'}`)
          .join(',');
        return `${key}=${statuses}`;
      }
      return `${key}=default`;
    })
    .join('|');

  const isDraftVideo = event?.isDraft === true;
  const eventId = event?.id;

  useEffect(() => {
    if (allItems.length === 0) return;
    const initial: Record<string, boolean> = {};
    allItems.forEach((item) => {
      const key = `${item.date.toISOString()}-${item.time}`;
      if (item.type === 'regular' && Array.isArray(item.data)) {
        const allActive = item.data.every(
          (pub) => (pub.pubStatus ?? 'scheduled') !== 'paused',
        );
        initial[key] = allActive;
      } else {
        initial[key] = !isDraftVideo;
      }
    });
    setCheckedDates(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, pubStatusSignature]);

  // ---------------------------------------------------------------------------
  // Computed values
  // ---------------------------------------------------------------------------
  const allDatesUnchecked = Object.values(checkedDates).every((v) => !v);
  const isReadOnly = event?.contentStatus === 'published';
  const isValidated =
    event?.contentStatus === 'to_publish' || event?.contentStatus === 'published';

  const isAbTestingEvent = event?.type === 'ab_testing';

  const hookVariations = isAbTestingEvent
    ? [
        {
          id: 'hook1',
          hookTitle: 'Version A',
          hookText: event.title,
          coverUrl: event.coverUrl,
          videoUrl: event.videoUrl,
        },
        ...(event.video2Url
          ? [
              {
                id: 'hook2',
                hookTitle: 'Version B',
                hookText: event.title,
                coverUrl: event.video2Thumbnail,
                videoUrl: event.video2Url,
              },
            ]
          : []),
        ...(event.video3Url
          ? [
              {
                id: 'hook3',
                hookTitle: 'Version C',
                hookText: event.title,
                coverUrl: event.video3Thumbnail,
                videoUrl: event.video3Url,
              },
            ]
          : []),
      ]
    : [
        {
          id: 'hook1',
          hookTitle: '',
          hookText: event?.description ?? '',
          coverUrl: event?.coverUrl,
          videoUrl: event?.videoUrl,
        },
      ];

  const rawInlineVideoKeys = (
    isAbTestingEvent
      ? [event?.videoUrl, event?.video2Url, event?.video3Url]
      : [event?.videoUrl]
  ).filter((k): k is string => Boolean(k));

  const videoCompatiblePlatforms = effectivePlatforms.filter(
    (p) =>
      p.connected &&
      ['instagram', 'tiktok', 'facebook', 'linkedin', 'youtube', 'threads'].includes(
        p.platform.toLowerCase(),
      ),
  );

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const toggleDateCheck = async (dateKey: string) => {
    const newChecked = { ...checkedDates, [dateKey]: !checkedDates[dateKey] };
    setCheckedDates(newChecked);

    const isNowChecked = newChecked[dateKey];
    const allUnchecked = Object.values(newChecked).every((v) => !v);

    const matchingItem = allItems.find((item) => {
      const key = `${item.date.toISOString()}-${item.time}`;
      return key === dateKey;
    });

    if (matchingItem?.type === 'regular' && Array.isArray(matchingItem.data)) {
      const newStatus = isNowChecked ? 'scheduled' : 'paused';
      await Promise.all(
        matchingItem.data
          .filter((pub) => pub.pubId)
          .map((pub) => updatePublication(pub.pubId!, { status: newStatus })),
      );
    }

    if (event?.id && (allUnchecked || (!allUnchecked && event?.isDraft))) {
      await updateVideoContent(event.id, { status: 'client_review' });
    }

    onValidate?.();
  };

  const handleValidate = async () => {
    if (!event?.id || isValidating) return;

    setIsValidating(true);
    try {
      const realVideoId = event.linkedContentId ?? event.id;

      let newStatus: string;
      if (isValidated) {
        newStatus = 'client_review';

        const pubsResult = await getPublicationsForVideo(realVideoId);
        const publications = pubsResult.data ?? [];
        const unpublished = publications.filter((p) => p.status !== 'published');

        for (const pub of unpublished) {
          try {
            if (userId) {
              await deleteMetricoolSchedule({
                userId,
                videoContentId: realVideoId,
                channel: pub.channel as Channel,
                scheduledAt: pub.scheduled_at ?? '',
                metricoolPostId: pub.metricool_post_id,
              });
            }
          } catch (e) {
            logger.warn(`[handleValidate] Failed to delete Metricool schedule for ${pub.channel}:`, e);
          }
          await updatePublication(pub.id, { status: 'paused' });
        }
      } else if (allDatesUnchecked) {
        newStatus = 'client_review';
      } else {
        newStatus = 'scheduled';
      }

      const result = await updateVideoContent(realVideoId, { status: newStatus });

      if (result.success) {
        onClose();
        onValidate?.();
      } else {
        logger.error('[useContentDetail] Error updating video status:', result.error);
      }
    } catch (error) {
      logger.error('[useContentDetail] Error updating video status:', error);
    } finally {
      setIsValidating(false);
    }
  };

  const handleSaveSchedule = async (
    publications: Array<{ channel: Channel; date: Date; time: string }>,
  ) => {
    if (!event) return false;
    const realVideoId = event.linkedContentId ?? event.id;
    const success = await updateVideoPublications(realVideoId, publications, userId ?? undefined);

    if (success) {
      const { dateForThisEvent, newCalendarId } = computePublicationEventDates(publications, event);

      onEventUpdate?.(event.id, {
        publicationTimes: publications,
        ...(newCalendarId && { calendarId: newCalendarId } as any),
        ...(dateForThisEvent && { date: dateForThisEvent }),
      });
      setScheduleModalOpen(false);
      toast.success(
        `${publications.length} date${publications.length > 1 ? 's' : ''} de publication mise${publications.length > 1 ? 's' : ''} à jour avec succès`,
      );
      onValidate?.();
    } else {
      toast.error('Erreur lors de la mise à jour des publications');
    }

    return success;
  };

  const handleValidateAndLaunch = async (
    publications: Array<{ channel: Channel; date: Date; time: string }>,
  ) => {
    if (!event) return;
    const realVideoId = event.linkedContentId ?? event.id;

    await updateVideoContent(realVideoId, { status: 'scheduled' });
    const success = await updateVideoPublications(realVideoId, publications, userId ?? undefined);

    if (success) {
      const { dateForThisEvent } = computePublicationEventDates(publications, event);

      onEventUpdate?.(event.id, {
        publicationTimes: publications,
        ...(dateForThisEvent && { date: dateForThisEvent }),
      });
      setScheduleModalOpen(false);
      onValidate?.();
      onClose();
    }
  };

  const handleClose = () => {
    // With InlineEditableV10, saves are explicit — no unsaved DOM state to check
    if (editingField !== null) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setEditingField(null);
    setShowCloseConfirm(false);
    onClose();
  };

  const handleOpenVideo = (videoUrl: string) => {
    setActiveVideoUrl(videoUrl);
  };

  const handleCloseVideo = () => {
    setActiveVideoUrl(null);
  };

  const handleCrossPost = async (channels: PublishChannel[]) => {
    if (!event?.id || !userId || isPublishing) return;

    setIsPublishing(true);
    setShowPublishMenu(false);

    try {
      const result = await crossPostToChannels({
        userId,
        videoContentId: event.id,
        channels,
        publishNow: true,
      });

      if (result.success) {
        const channelNames = channels.join(', ');
        toast.success(`Contenu programmé sur ${channels.length} réseaux`, {
          description: `${channelNames} - sera publié dans ~5 minutes`,
        });
      } else {
        toast.error('Erreur de publication', {
          description: result.error ?? 'Une erreur est survenue',
        });
      }
    } catch (error) {
      logger.error('[useContentDetail] Error cross-posting:', error);
      toast.error('Erreur de publication');
    } finally {
      setIsPublishing(false);
    }
  };

  const handleCrosspostToggle = async (enabled: boolean) => {
    if (!event?.id) return;
    const newDisabled = !enabled;
    setCrosspostDisabled(newDisabled);
    const result = await updateVideoContentInstagramCrosspost(event.id, newDisabled);
    if (!result.success) {
      setCrosspostDisabled(!newDisabled);
      toast.error('Erreur lors de la mise à jour du cross-post');
    }
  };

  const toggleVersionEnabled = async (index: number) => {
    if (!event?.id) return;
    const versionKey = (['a', 'b', 'c'] as const)[index];
    const newEnabled = !versionsEnabled[index];
    // Use the real video UUID — AB testing event IDs carry an `_ab` suffix
    const realVideoId = event.linkedContentId ?? event.id;

    setVersionsEnabled((prev) => {
      const next = [...prev];
      next[index] = newEnabled;
      return next;
    });

    const result = await updateVideoContentVersionEnabled(realVideoId, versionKey, newEnabled);
    if (!result.success) {
      setVersionsEnabled((prev) => {
        const next = [...prev];
        next[index] = !newEnabled;
        return next;
      });
      toast.error('Erreur lors de la mise à jour de la version');
    } else {
      const patchKey = (
        ['versionAEnabled', 'versionBEnabled', 'versionCEnabled'] as const
      )[index];
      onEventUpdate?.(event.id, { [patchKey]: newEnabled });
    }
  };

  const saveTitle = async (val: string) => {
    setEditTitle(val);
    setEditingField(null);
    if (event?.id && val !== event.title) {
      await updateVideoContent(event.id, { title: val });
      supabase.functions
        .invoke('description-update-webhook', {
          body: { video_content_id: event.id, updated_field: 'title' },
        })
        .catch((err) => logger.warn('[useContentDetail] Title webhook failed:', err));
      onValidate?.();
    }
  };

  const saveDescription = async (val: string) => {
    setEditDescription(val);
    setEditingField(null);
    if (event?.id && val !== event.description) {
      await updateVideoContent(event.id, { description: val });
      supabase.functions
        .invoke('description-update-webhook', {
          body: { video_content_id: event.id, updated_field: 'description' },
        })
        .catch((err) => logger.warn('[useContentDetail] Description webhook failed:', err));
      onValidate?.();
    }
  };

  return {
    // State
    editingField,
    setEditingField,
    editTitle,
    editDescription,
    scheduleModalOpen,
    setScheduleModalOpen,
    showCloseConfirm,
    setShowCloseConfirm,
    allItems,
    checkedDates,
    isValidating,
    isPublishing,
    showPublishMenu,
    setShowPublishMenu,
    globalCrosspostEnabled,
    crosspostDisabled,
    versionsEnabled,
    ownerPlatforms,
    activeVideoUrl,
    // Computed
    effectivePlatforms,
    isAbTestingEvent,
    hookVariations,
    allDatesUnchecked,
    isReadOnly,
    isValidated,
    rawInlineVideoKeys,
    videoCompatiblePlatforms,
    userProfile,
    // Handlers
    toggleDateCheck,
    handleValidate,
    handleSaveSchedule,
    handleValidateAndLaunch,
    handleClose,
    handleConfirmClose,
    handleOpenVideo,
    handleCloseVideo,
    handleCrossPost,
    handleCrosspostToggle,
    toggleVersionEnabled,
    saveTitle,
    saveDescription,
  };
}
